import React from 'react';
import {
  AdminInboundReplyTemplateCategory,
  AdminInboundReplyTemplate,
  inboundInboxService,
} from '../../services/inboundInboxService';

type Props = {};

type Draft = {
  key: string;
  name: string;
  categoryKey: string;
  previewText: string;
  bodyHtml: string;
  mailboxKey: string;
  emailCategory: string;
  isActive: boolean;
  sortOrder: number;
};

const emptyDraft: Draft = {
  key: '',
  name: '',
  categoryKey: 'general',
  previewText: '',
  bodyHtml: '',
  mailboxKey: '',
  emailCategory: '',
  isActive: true,
  sortOrder: 100,
};

const fallbackCategoryOptions = [
  { key: 'general', name: 'General' },
  { key: 'need_info', name: 'Need Info' },
  { key: 'quote', name: 'Quote' },
  { key: 'follow_up', name: 'Follow Up' },
  { key: 'custom', name: 'Custom' },
];
const keyPattern = /^[a-z0-9][a-z0-9_-]{1,63}$/;
const parseApiError = (error: any): string => {
  const apiMessage = String(error?.response?.data?.error || error?.response?.data?.message || '').trim();
  if (apiMessage) return apiMessage;
  return String(error?.message || '').trim() || 'Request failed';
};
const toTemplateKey = (value: string): string =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);

export const InboundReplyTemplatesTab: React.FC<Props> = () => {
  const [items, setItems] = React.useState<AdminInboundReplyTemplate[]>([]);
  const [categories, setCategories] = React.useState<AdminInboundReplyTemplateCategory[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [status, setStatus] = React.useState<string>('');
  const [query, setQuery] = React.useState('');
  const [category, setCategory] = React.useState('');
  const [activeFilter, setActiveFilter] = React.useState<'ALL' | 'ACTIVE' | 'DISABLED'>('ALL');
  const [selectedId, setSelectedId] = React.useState<string>('');
  const [createMode, setCreateMode] = React.useState(false);
  const [draft, setDraft] = React.useState<Draft>(emptyDraft);
  const [categoryKeyDraft, setCategoryKeyDraft] = React.useState('');
  const [categoryNameDraft, setCategoryNameDraft] = React.useState('');
  const [categorySortDraft, setCategorySortDraft] = React.useState(100);

  const selected = React.useMemo(
    () => items.find(item => item.id === selectedId) || null,
    [items, selectedId],
  );
  const categoryOptions = React.useMemo(
    () => (categories.length ? categories : fallbackCategoryOptions.map(item => ({ id: item.key, key: item.key, name: item.name, sortOrder: 100, isActive: true, createdAt: null, updatedAt: null }))),
    [categories],
  );

  const loadCategories = React.useCallback(async () => {
    try {
      const rows = await inboundInboxService.getAdminInboundReplyTemplateCategories();
      setCategories(rows);
    } catch {
      setCategories([]);
    }
  }, []);

  const load = React.useCallback(async () => {
    setLoading(true);
    setStatus('');
    try {
      const rows = await inboundInboxService.getAdminInboundReplyTemplates({
        q: query || undefined,
        category: category || undefined,
        isActive: activeFilter === 'ALL' ? undefined : activeFilter === 'ACTIVE',
      });
      setItems(rows);
      setSelectedId(prev => prev && rows.some(row => row.id === prev) ? prev : (rows[0]?.id || ''));
    } catch (error: any) {
      setStatus(error?.message || 'Failed to load templates');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [activeFilter, category, query]);

  React.useEffect(() => {
    void load();
    void loadCategories();
  }, [load, loadCategories]);

  const resetDraft = React.useCallback(() => {
    setDraft(emptyDraft);
    setCreateMode(false);
  }, []);

  const startCreate = React.useCallback(() => {
    setCreateMode(true);
    setSelectedId('');
    setDraft(emptyDraft);
  }, []);

  const startEdit = React.useCallback((item: AdminInboundReplyTemplate) => {
    setCreateMode(false);
    setSelectedId(item.id);
    setDraft({
      key: item.key,
      name: item.name,
      categoryKey: item.categoryKey || 'general',
      previewText: item.previewText,
      bodyHtml: item.bodyHtml,
      mailboxKey: item.mailboxKey || '',
      emailCategory: item.emailCategory || '',
      isActive: item.isActive,
      sortOrder: item.sortOrder,
    });
  }, []);

  const startDuplicate = React.useCallback((item: AdminInboundReplyTemplate) => {
    setCreateMode(true);
    setSelectedId('');
    setDraft({
      key: `${item.key}_copy`,
      name: `${item.name} Copy`,
      categoryKey: item.categoryKey || 'general',
      previewText: item.previewText,
      bodyHtml: item.bodyHtml,
      mailboxKey: item.mailboxKey || '',
      emailCategory: item.emailCategory || '',
      isActive: item.isActive,
      sortOrder: item.sortOrder + 10,
    });
  }, []);

  const saveCreate = React.useCallback(async () => {
    const key = toTemplateKey(draft.key || draft.name);
    const name = draft.name.trim();
    const categoryKey = String(draft.categoryKey || '').trim().toLowerCase();
    const categoryRow = categoryOptions.find(item => String(item.key || '').trim().toLowerCase() === categoryKey);
    const category = (categoryRow?.name || 'General').toUpperCase();
    const previewText = draft.previewText.trim();
    const bodyHtml = draft.bodyHtml.trim();
    if (!keyPattern.test(key)) {
      setStatus('Invalid key. Use 2-64 chars: lowercase letters, numbers, underscore, hyphen.');
      return;
    }
    if (!name) {
      setStatus('Template name is required.');
      return;
    }
    if (!previewText) {
      setStatus('Preview text is required.');
      return;
    }
    if (!bodyHtml) {
      setStatus('Body HTML is required.');
      return;
    }
    if (!categoryKey) {
      setStatus('Category is required.');
      return;
    }
    setSaving(true);
    setStatus('');
    try {
      const created = await inboundInboxService.createAdminInboundReplyTemplate({
        key,
        name,
        category,
        categoryKey,
        previewText,
        bodyHtml,
        mailboxKey: draft.mailboxKey.trim() || null,
        emailCategory: draft.emailCategory.trim() || null,
        isActive: draft.isActive,
        sortOrder: Number(draft.sortOrder || 0),
      });
      setStatus('Template created');
      setCreateMode(false);
      await load();
      await loadCategories();
      setSelectedId(created.id);
    } catch (error: any) {
      setStatus(parseApiError(error));
    } finally {
      setSaving(false);
    }
  }, [categoryOptions, draft, load, loadCategories]);

  const saveUpdate = React.useCallback(async () => {
    if (!selected) return;
    const name = draft.name.trim();
    const categoryKey = String(draft.categoryKey || '').trim().toLowerCase();
    const categoryRow = categoryOptions.find(item => String(item.key || '').trim().toLowerCase() === categoryKey);
    const category = (categoryRow?.name || 'General').toUpperCase();
    const previewText = draft.previewText.trim();
    const bodyHtml = draft.bodyHtml.trim();
    if (!name) {
      setStatus('Template name is required.');
      return;
    }
    if (!previewText) {
      setStatus('Preview text is required.');
      return;
    }
    if (!bodyHtml) {
      setStatus('Body HTML is required.');
      return;
    }
    if (!categoryKey) {
      setStatus('Category is required.');
      return;
    }
    setSaving(true);
    setStatus('');
    try {
      await inboundInboxService.updateAdminInboundReplyTemplate(selected.id, {
        name,
        category,
        categoryKey,
        previewText,
        bodyHtml,
        mailboxKey: draft.mailboxKey.trim() || null,
        emailCategory: draft.emailCategory.trim() || null,
        isActive: draft.isActive,
        sortOrder: Number(draft.sortOrder || 0),
      });
      setStatus('Template updated');
      await load();
    } catch (error: any) {
      setStatus(parseApiError(error));
    } finally {
      setSaving(false);
    }
  }, [categoryOptions, draft, load, selected]);

  const toggleActive = React.useCallback(async (item: AdminInboundReplyTemplate, active: boolean) => {
    setSaving(true);
    setStatus('');
    try {
      await inboundInboxService.updateAdminInboundReplyTemplate(item.id, { isActive: active });
      setStatus(active ? 'Template enabled' : 'Template disabled');
      await load();
      if (selectedId === item.id) {
        setDraft(prev => ({ ...prev, isActive: active }));
      }
    } catch (error: any) {
      setStatus(parseApiError(error));
    } finally {
      setSaving(false);
    }
  }, [load, selectedId]);

  React.useEffect(() => {
    if (!selected || createMode) return;
    setDraft({
      key: selected.key,
      name: selected.name,
      categoryKey: selected.categoryKey || 'general',
      previewText: selected.previewText,
      bodyHtml: selected.bodyHtml,
      mailboxKey: selected.mailboxKey || '',
      emailCategory: selected.emailCategory || '',
      isActive: selected.isActive,
      sortOrder: selected.sortOrder,
    });
  }, [createMode, selected]);

  const createCategory = React.useCallback(async () => {
    const key = toTemplateKey(categoryKeyDraft || categoryNameDraft);
    const name = categoryNameDraft.trim();
    if (!keyPattern.test(key)) {
      setStatus('Invalid category key.');
      return;
    }
    if (!name) {
      setStatus('Category name is required.');
      return;
    }
    setSaving(true);
    setStatus('');
    try {
      await inboundInboxService.createAdminInboundReplyTemplateCategory({
        key,
        name,
        sortOrder: Number(categorySortDraft || 0),
        isActive: true,
      });
      setStatus('Category created');
      setCategoryKeyDraft('');
      setCategoryNameDraft('');
      setCategorySortDraft(100);
      await loadCategories();
    } catch (error: any) {
      setStatus(parseApiError(error));
    } finally {
      setSaving(false);
    }
  }, [categoryKeyDraft, categoryNameDraft, categorySortDraft, loadCategories]);

  const updateCategory = React.useCallback(async (item: AdminInboundReplyTemplateCategory, patch: { name?: string; sortOrder?: number; isActive?: boolean }) => {
    setSaving(true);
    setStatus('');
    try {
      await inboundInboxService.updateAdminInboundReplyTemplateCategory(item.id, patch);
      setStatus('Category updated');
      await loadCategories();
    } catch (error: any) {
      setStatus(parseApiError(error));
    } finally {
      setSaving(false);
    }
  }, [loadCategories]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search templates..."
          className="h-9 min-w-[220px] rounded-lg border border-slate-200 px-3 text-sm text-slate-700"
        />
        <select
          value={category}
          onChange={(event) => setCategory(event.target.value)}
          className="h-9 rounded-lg border border-slate-200 px-2 text-sm text-slate-700"
        >
          <option value="">All categories</option>
          {categoryOptions.map(option => <option key={option.key} value={option.key}>{option.name}</option>)}
        </select>
        <select
          value={activeFilter}
          onChange={(event) => setActiveFilter(event.target.value as any)}
          className="h-9 rounded-lg border border-slate-200 px-2 text-sm text-slate-700"
        >
          <option value="ALL">All</option>
          <option value="ACTIVE">Active</option>
          <option value="DISABLED">Disabled</option>
        </select>
        <button type="button" onClick={() => void load()} className="h-9 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-700">
          Refresh
        </button>
        <button type="button" onClick={startCreate} className="h-9 rounded-lg bg-slate-900 px-3 text-xs font-semibold text-white">
          Create Template
        </button>
      </div>

      {status ? (
        <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">{status}</div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
        <div className="min-h-[420px] overflow-auto rounded-xl border border-slate-200 p-2">
          {loading ? <div className="px-2 py-3 text-sm text-slate-500">Loading…</div> : null}
          {!loading && items.length === 0 ? <div className="px-2 py-3 text-sm text-slate-500">No templates found.</div> : null}
          {!loading && items.map(item => (
            <button
              key={item.id}
              type="button"
              onClick={() => startEdit(item)}
              className={`mb-1 w-full rounded-lg border px-2.5 py-2 text-left transition ${
                selectedId === item.id && !createMode ? 'border-slate-800 bg-slate-50' : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold text-slate-800">{item.name}</span>
                <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${item.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>
                  {item.isActive ? 'Active' : 'Disabled'}
                </span>
              </div>
              <div className="mt-1 text-[10px] text-slate-500">{item.categoryName || item.category || item.categoryKey || '-'} • key: {item.key}</div>
            </button>
          ))}
        </div>

        <div className="rounded-xl border border-slate-200 p-4">
          <div className="mb-3 text-sm font-semibold text-slate-900">{createMode ? 'Create Template' : selected ? 'Edit Template' : 'Select a template'}</div>
          <div className="space-y-3">
            <label className="block text-xs font-semibold text-slate-600">Key (immutable)</label>
            <input
              value={draft.key}
              disabled={!createMode}
              onChange={(event) => setDraft(prev => ({ ...prev, key: toTemplateKey(event.target.value) }))}
              placeholder="example: follow_up"
              className="h-9 w-full rounded-lg border border-slate-200 px-3 text-sm text-slate-700 disabled:bg-slate-50"
            />

            <label className="block text-xs font-semibold text-slate-600">Name</label>
            <input value={draft.name} onChange={(event) => setDraft(prev => ({ ...prev, name: event.target.value }))} className="h-9 w-full rounded-lg border border-slate-200 px-3 text-sm text-slate-700" />

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-semibold text-slate-600">Category</label>
                <select value={draft.categoryKey} onChange={(event) => setDraft(prev => ({ ...prev, categoryKey: event.target.value }))} className="h-9 w-full rounded-lg border border-slate-200 px-2 text-sm text-slate-700">
                  {categoryOptions.map(option => <option key={option.key} value={option.key}>{option.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600">Sort Order</label>
                <input type="number" value={draft.sortOrder} onChange={(event) => setDraft(prev => ({ ...prev, sortOrder: Number(event.target.value || 0) }))} className="h-9 w-full rounded-lg border border-slate-200 px-3 text-sm text-slate-700" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-semibold text-slate-600">Mailbox Key (optional)</label>
                <input value={draft.mailboxKey} onChange={(event) => setDraft(prev => ({ ...prev, mailboxKey: event.target.value }))} className="h-9 w-full rounded-lg border border-slate-200 px-3 text-sm text-slate-700" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600">Email Category (optional)</label>
                <input value={draft.emailCategory} onChange={(event) => setDraft(prev => ({ ...prev, emailCategory: event.target.value }))} className="h-9 w-full rounded-lg border border-slate-200 px-3 text-sm text-slate-700" />
              </div>
            </div>

            <label className="block text-xs font-semibold text-slate-600">Preview Text</label>
            <input value={draft.previewText} onChange={(event) => setDraft(prev => ({ ...prev, previewText: event.target.value }))} className="h-9 w-full rounded-lg border border-slate-200 px-3 text-sm text-slate-700" />

            <label className="block text-xs font-semibold text-slate-600">Body HTML</label>
            <textarea value={draft.bodyHtml} onChange={(event) => setDraft(prev => ({ ...prev, bodyHtml: event.target.value }))} rows={8} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700" />

            <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600">
              <input type="checkbox" checked={draft.isActive} onChange={(event) => setDraft(prev => ({ ...prev, isActive: event.target.checked }))} />
              Active
            </label>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            {createMode ? (
              <button type="button" disabled={saving} onClick={() => void saveCreate()} className="h-9 rounded-lg bg-slate-900 px-3 text-xs font-semibold text-white disabled:opacity-50">Create</button>
            ) : (
              <button type="button" disabled={saving || !selected} onClick={() => void saveUpdate()} className="h-9 rounded-lg bg-slate-900 px-3 text-xs font-semibold text-white disabled:opacity-50">Save</button>
            )}
            <button type="button" disabled={saving} onClick={resetDraft} className="h-9 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-700">Cancel</button>
            {!createMode && selected ? (
              <>
                <button type="button" disabled={saving} onClick={() => startDuplicate(selected)} className="h-9 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-700">Duplicate</button>
                {selected.isActive ? (
                  <button type="button" disabled={saving} onClick={() => void toggleActive(selected, false)} className="h-9 rounded-lg border border-amber-300 bg-amber-50 px-3 text-xs font-semibold text-amber-700">Disable</button>
                ) : (
                  <button type="button" disabled={saving} onClick={() => void toggleActive(selected, true)} className="h-9 rounded-lg border border-emerald-300 bg-emerald-50 px-3 text-xs font-semibold text-emerald-700">Enable</button>
                )}
              </>
            ) : null}
          </div>
        </div>
      </div>
      <div className="mt-4 rounded-xl border border-slate-200 p-4">
        <div className="mb-3 text-sm font-semibold text-slate-900">Category Management</div>
        <div className="mb-3 grid grid-cols-1 gap-2 md:grid-cols-[1fr_1fr_120px_auto]">
          <input
            value={categoryKeyDraft}
            onChange={(event) => setCategoryKeyDraft(toTemplateKey(event.target.value))}
            placeholder="key (slug)"
            className="h-9 rounded-lg border border-slate-200 px-3 text-sm text-slate-700"
          />
          <input
            value={categoryNameDraft}
            onChange={(event) => setCategoryNameDraft(event.target.value)}
            placeholder="display name"
            className="h-9 rounded-lg border border-slate-200 px-3 text-sm text-slate-700"
          />
          <input
            type="number"
            value={categorySortDraft}
            onChange={(event) => setCategorySortDraft(Number(event.target.value || 0))}
            className="h-9 rounded-lg border border-slate-200 px-3 text-sm text-slate-700"
          />
          <button type="button" disabled={saving} onClick={() => void createCategory()} className="h-9 rounded-lg bg-slate-900 px-3 text-xs font-semibold text-white disabled:opacity-50">
            Add Category
          </button>
        </div>
        <div className="space-y-2">
          {categoryOptions.map((item) => (
            <div key={item.key} className="grid grid-cols-1 items-center gap-2 rounded-lg border border-slate-200 p-2 md:grid-cols-[200px_1fr_100px_auto_auto]">
              <div className="text-xs font-semibold text-slate-700">{item.key}</div>
              <input
                defaultValue={item.name}
                onBlur={(event) => {
                  const next = event.target.value.trim();
                  if (next && next !== item.name) void updateCategory(item as any, { name: next });
                }}
                className="h-8 rounded border border-slate-200 px-2 text-xs text-slate-700"
              />
              <input
                type="number"
                defaultValue={item.sortOrder}
                onBlur={(event) => {
                  const next = Number(event.target.value || 0);
                  if (next !== item.sortOrder) void updateCategory(item as any, { sortOrder: next });
                }}
                className="h-8 rounded border border-slate-200 px-2 text-xs text-slate-700"
              />
              <span className={`text-[10px] font-semibold ${item.isActive ? 'text-emerald-700' : 'text-slate-500'}`}>{item.isActive ? 'Active' : 'Disabled'}</span>
              <button
                type="button"
                disabled={saving}
                onClick={() => void updateCategory(item as any, { isActive: !item.isActive })}
                className="h-8 rounded border border-slate-200 px-2 text-[10px] font-semibold text-slate-700"
              >
                {item.isActive ? 'Disable' : 'Enable'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
